import React, { useState, useEffect, useCallback } from 'react';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { API_ENDPOINTS } from '../../config/api';
import SecretShopTab from '../Customer/SecretShopTab';

const G = '#c9a962'; // gold accent
const BG = '#0b0b0f';
const CARD = '#15151c';
const BORDER = 'rgba(201,169,98,0.25)';
const TEXT = '#f2ede2';
const TEXT_DIM = '#9a9488';

const shell = {
  minHeight: '100vh',
  background: `radial-gradient(ellipse at top, #1a1a22 0%, ${BG} 60%)`,
  color: TEXT,
  fontFamily: "'Georgia', 'Times New Roman', serif",
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '48px 20px 80px',
};

const wordmark = {
  fontSize: 13,
  letterSpacing: '0.3em',
  textTransform: 'uppercase',
  color: G,
  marginBottom: 40,
  textAlign: 'center',
};

const card = {
  background: CARD,
  border: `1px solid ${BORDER}`,
  borderRadius: 4,
  padding: '36px 32px',
  width: '100%',
  maxWidth: 420,
  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
};

const h1 = {
  fontSize: 24,
  fontWeight: 400,
  margin: '0 0 8px 0',
  letterSpacing: '0.02em',
};

const sub = {
  fontSize: 13,
  color: TEXT_DIM,
  margin: '0 0 28px 0',
  fontFamily: "'Helvetica Neue', Arial, sans-serif",
  lineHeight: 1.5,
};

const label = {
  fontSize: 11,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: TEXT_DIM,
  fontFamily: "'Helvetica Neue', Arial, sans-serif",
  display: 'block',
  marginBottom: 6,
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  background: '#0e0e13',
  border: `1px solid ${BORDER}`,
  borderRadius: 3,
  padding: '12px 14px',
  color: TEXT,
  fontSize: 15,
  fontFamily: "'Helvetica Neue', Arial, sans-serif",
  marginBottom: 18,
};

const button = {
  width: '100%',
  background: G,
  color: '#1a1408',
  border: 'none',
  borderRadius: 3,
  padding: '13px 18px',
  fontSize: 13,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  fontWeight: 600,
  fontFamily: "'Helvetica Neue', Arial, sans-serif",
  cursor: 'pointer',
};

const errorStyle = {
  color: '#e0a0a0',
  fontSize: 13,
  fontFamily: "'Helvetica Neue', Arial, sans-serif",
  marginBottom: 16,
};

const linkBtn = {
  background: 'transparent',
  border: 'none',
  color: G,
  fontSize: 13,
  fontFamily: "'Helvetica Neue', Arial, sans-serif",
  cursor: 'pointer',
  textDecoration: 'underline',
  padding: 0,
  marginTop: 18,
};

const JoinScreen = () => {
  const { signupWithCode } = useCustomerAuth();
  const [mode, setMode] = useState('join'); // 'join' | 'login'
  const [code, setCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!code.trim() || !firstName.trim()) {
      setError('Enter your code and your name');
      return;
    }
    setLoading(true);
    try {
      await signupWithCode(code.trim(), firstName.trim(), phone.trim());
    } catch (err) {
      setError(err.message || 'That code did not work.');
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'login') {
    return <PhoneLoginScreen onBack={() => setMode('join')} />;
  }

  return (
    <div style={shell}>
      <div style={wordmark}>◆ Members Only ◆</div>
      <div style={card}>
        <h1 style={h1}>Request Access</h1>
        <p style={sub}>Enter the code you were given and your name. No email, no password required.</p>
        {error && <p style={errorStyle}>{error}</p>}
        <form onSubmit={handleSubmit}>
          <label style={label}>Access Code</label>
          <input
            style={{ ...inputStyle, textTransform: 'uppercase', letterSpacing: '0.1em' }}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoCapitalize="characters"
            autoCorrect="off"
            autoComplete="off"
          />
          <label style={label}>Your Name</label>
          <input style={inputStyle} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <label style={label}>Phone (optional)</label>
          <input style={inputStyle} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Lets you log back in later" />
          <button type="submit" style={button} disabled={loading}>
            {loading ? 'Entering…' : 'Enter'}
          </button>
        </form>
        <button type="button" style={linkBtn} onClick={() => setMode('login')}>
          Already a member? Log back in with your phone →
        </button>
      </div>
    </div>
  );
};

const PhoneLoginScreen = ({ onBack }) => {
  const { loginWithPhone } = useCustomerAuth();
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!phone.trim()) {
      setError('Enter the phone number you gave when you joined');
      return;
    }
    setLoading(true);
    try {
      await loginWithPhone(phone.trim());
    } catch (err) {
      setError(err.message || 'No account found for that phone number.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={shell}>
      <div style={wordmark}>◆ Members Only ◆</div>
      <div style={card}>
        <h1 style={h1}>Log Back In</h1>
        <p style={sub}>
          Only works if you gave a phone number when you joined. Otherwise you'll need a new access code.
        </p>
        {error && <p style={errorStyle}>{error}</p>}
        <form onSubmit={handleSubmit}>
          <label style={label}>Phone Number</label>
          <input style={inputStyle} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoFocus />
          <button type="submit" style={button} disabled={loading}>
            {loading ? 'Checking…' : 'Log In'}
          </button>
        </form>
        <button type="button" style={linkBtn} onClick={onBack}>
          ← Back to access code
        </button>
      </div>
    </div>
  );
};

const MessagesPanel = () => {
  const { getAuthHeaders } = useCustomerAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.customerProfile.conversations, { headers: getAuthHeaders() });
      const data = await res.json();
      const latest = (data.conversations || [])[0];
      setMessages(latest?.messages || []);
    } catch {
      // Non-fatal — panel just stays empty.
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return null;
  if (messages.length === 0) return null;

  return (
    <div
      style={{
        background: CARD,
        border: `1px solid ${BORDER}`,
        borderRadius: 4,
        padding: 20,
        marginTop: 20,
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: TEXT_DIM, marginBottom: 14 }}>
        Messages
      </div>
      {messages.map((m) => (
        <div key={m.id} style={{ borderTop: `1px solid ${BORDER}`, padding: '10px 0', fontSize: 14 }}>
          <div style={{ color: m.role === 'assistant' ? G : TEXT, marginBottom: 2 }}>{m.content}</div>
          <div style={{ fontSize: 11, color: TEXT_DIM }}>{new Date(m.timestamp).toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
};

const CatalogScreen = () => {
  const { customer, logout } = useCustomerAuth();

  const handleLogout = () => {
    const warning = customer?.phone
      ? "Log out? You'll be able to log back in with your phone number."
      : "Log out? You didn't give a phone number, so you'll need a brand new access code to get back in — you won't be able to track this order anymore.";
    if (window.confirm(warning)) logout();
  };

  return (
    <div style={shell}>
      <div style={{ width: '100%', maxWidth: 560 }}>
        <div style={wordmark}>◆ Members Only ◆</div>
        <h1 style={{ ...h1, textAlign: 'center', marginBottom: 24 }}>Exclusive Products</h1>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
            fontFamily: "'Helvetica Neue', Arial, sans-serif",
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Welcome
            </div>
            <div style={{ fontSize: 18, color: TEXT }}>{customer?.firstName || 'Member'}</div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={logout}
              style={{
                fontSize: 12,
                color: TEXT_DIM,
                background: 'transparent',
                border: `1px solid ${BORDER}`,
                borderRadius: 3,
                padding: '8px 14px',
                cursor: 'pointer',
              }}
            >
              Log out
            </button>
          </div>
        </div>
        <div
          style={{
            background: CARD,
            border: `1px solid ${BORDER}`,
            borderRadius: 4,
            padding: 20,
            fontFamily: "'Helvetica Neue', Arial, sans-serif",
          }}
        >
          <SecretShopTab />
        </div>
        <MessagesPanel />
      </div>
    </div>
  );
};

const ExclusiveApp = () => {
  const { isAuthenticated, isLoading } = useCustomerAuth();
  // Only gate on the *initial* token-restore check, not every later signup/login call —
  // otherwise JoinScreen unmounts (losing its own error state) on every failed attempt,
  // since the auth context flips isLoading true/false around every request it makes.
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    if (!isLoading) setHasHydrated(true);
  }, [isLoading]);

  if (!hasHydrated) {
    return <div style={shell} />;
  }

  return isAuthenticated ? <CatalogScreen /> : <JoinScreen />;
};

export default ExclusiveApp;
